"use client";

import React from "react";
import Link from "next/link";
import Logo from "./Logo";
import { COMPANY_INFO } from "@/data/companyInfo";

interface FooterLink {
  label: string;
  href: string;
  isExternal?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const FOOTER_SECTIONS: FooterSection[] = [
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/about" },
      { label: "Services Hub", href: "/services" },
      { label: "Portfolio & Work", href: "/portfolio" },
      { label: "Engineering Blog", href: "/blog" },
      { label: "Careers & Contact", href: "/contact" },
    ],
  },
  {
    title: "Support & Legal",
    links: [
      { label: "Book Scoping Call", href: "/book-consultation" },
      { label: "Terms of Service", href: "/terms-of-service" },
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Email Direct", href: `mailto:${COMPANY_INFO.email}`, isExternal: true },
      { label: "WhatsApp Instant", href: COMPANY_INFO.whatsappLink, isExternal: true },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-[#1C1714] text-slate-200 border-t-4 border-primary relative z-10 overflow-hidden">
      <div className="w-full px-margin-mobile md:px-gutter py-10 sm:py-16 md:py-20 max-w-container-max mx-auto flex flex-col lg:flex-row justify-between gap-8 sm:gap-12">
        {/* Brand & Description Column */}
        <div className="max-w-md">
          <div className="mb-6">
            <Link href="/" aria-label="Beyond Ambition Home">
              <Logo inverse={true} />
            </Link>
          </div>
          <p className="text-slate-300 text-sm sm:text-base mb-6 leading-relaxed">
            High-performance engineering for the AI era. Building the digital infrastructure that powers the next generation of global industry leaders across NZ, Australia, and the US.
          </p>
          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
            Software engineering agency in Auckland, New Zealand — serving clients across New Zealand, Australia, United States, and worldwide.
          </p>
        </div>

        {/* Dynamic Navigation & Contact Columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 w-full lg:w-auto">
          {/* Looped Navigation Sections */}
          {FOOTER_SECTIONS.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs text-white uppercase mb-5 font-bold tracking-widest border-b border-white/10 pb-2">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.isExternal ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-300 hover:text-primary transition-colors text-sm font-medium"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        href={link.href}
                        className="text-slate-300 hover:text-primary transition-colors text-sm font-medium"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Details & Business Entity Column */}
          <div>
            <h3 className="text-xs text-white uppercase mb-5 font-bold tracking-widest border-b border-white/10 pb-2">
              Contact & Entity
            </h3>
            <p className="text-slate-300 text-sm mb-3">
              <a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-primary transition-colors font-medium">
                {COMPANY_INFO.email}
              </a>
            </p>
            <p className="text-slate-300 text-sm mb-3">
              <a href={COMPANY_INFO.phoneLink} className="hover:text-primary transition-colors font-medium">
                {COMPANY_INFO.phoneFormatted}
              </a>
            </p>
            <p className="text-slate-400 text-xs leading-relaxed mb-3">
              <a
                href={COMPANY_INFO.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                {COMPANY_INFO.addressLine1}
                <br />
                {COMPANY_INFO.addressLine2}
              </a>
            </p>

            <div className="pt-2 border-t border-white/10 text-xs text-slate-400 space-y-1">
              <p className="font-bold text-slate-300">{COMPANY_INFO.name}</p>
              <p>
                NZBN:{" "}
                <a
                  href={COMPANY_INFO.nzbnUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300 hover:underline font-mono"
                >
                  {COMPANY_INFO.nzbn}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Copyright Bar */}
      <div className="w-full px-margin-mobile md:px-gutter py-6 border-t border-white/10 max-w-container-max mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
        <p>© 2026 Beyond Ambition Limited. All rights reserved.</p>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-slate-400">Auckland, NZ • Sydney, AU • San Francisco, US</p>
          <span className="hidden sm:inline text-white/20">•</span>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("open-cookie-settings"));
              }
            }}
            className="text-slate-400 hover:text-primary transition-colors cursor-pointer underline underline-offset-4"
          >
            Cookie Preferences
          </button>
        </div>
      </div>
    </footer>
  );
}
